import type { AppShellSnapshot } from "../ux/AppShell";

const DB_NAME = "discus-ai-v2-shell";
const DB_VERSION = 1;
const STORE_NAME = "shell_snapshots";
const CACHE_VERSION = 3;

export interface CachedShellSnapshot {
  userId: string;
  farmId: string;
  version: number;
  cachedAt: string;
  snapshot: AppShellSnapshot;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("SNAPSHOT_CACHE_REQUEST_FAILED"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("SNAPSHOT_CACHE_TRANSACTION_FAILED"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("SNAPSHOT_CACHE_TRANSACTION_ABORTED"));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "userId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("SNAPSHOT_CACHE_OPEN_FAILED"));
    request.onblocked = () => reject(new Error("SNAPSHOT_CACHE_OPEN_BLOCKED"));
  });
}

export async function readCachedShellSnapshot(
  userId: string,
): Promise<CachedShellSnapshot | undefined> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const record = await requestResult(
      transaction.objectStore(STORE_NAME).get(userId) as IDBRequest<
        CachedShellSnapshot | undefined
      >,
    );
    await transactionDone(transaction);

    if (!record || record.version !== CACHE_VERSION) {
      return undefined;
    }

    return structuredClone(record);
  } finally {
    database.close();
  }
}

export async function writeCachedShellSnapshot(
  userId: string,
  farmId: string,
  snapshot: AppShellSnapshot,
): Promise<void> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put({
      userId,
      farmId,
      version: CACHE_VERSION,
      cachedAt: new Date().toISOString(),
      snapshot: structuredClone(snapshot),
    } satisfies CachedShellSnapshot);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function clearCachedShellSnapshot(userId: string): Promise<void> {
  const database = await openDatabase();

  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).delete(userId);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}
